const AUTH_WAIT_RETRIES = 120;
const AUTH_WAIT_INTERVAL_MS = 50;

export default function (context) {
  const { app, store, $auth: contextAuth } = context;

  const attemptInit = (attempt = 0) => {
    const auth = getAuthInstance(app, contextAuth);

    if (!auth) {
      if (attempt < AUTH_WAIT_RETRIES) {
        setTimeout(() => attemptInit(attempt + 1), AUTH_WAIT_INTERVAL_MS);
      } else {
        console.warn('[User Profile Init] Auth was not available after waiting');
      }
      return;
    }

    ensureAuthWatcher(auth, store);
    initializeUserProfile(auth, store);
  };

  if (process.client) {
    attemptInit();
  }

  if (app.router) {
    app.router.afterEach(() => {
      attemptInit();
    });
  }
}

function getAuthInstance(app, contextAuth) {
  if (contextAuth) {
    return contextAuth;
  }

  if (app && app.$auth) {
    return app.$auth;
  }

  if (typeof window !== 'undefined' && window.$nuxt && window.$nuxt.$auth) {
    return window.$nuxt.$auth;
  }

  return null;
}

function ensureAuthWatcher(auth, store) {
  if (!auth || !auth.$storage || auth.__userProfileInitWatcherRegistered) {
    return;
  }

  const existingPerm = auth.$storage.getState('perm');
  if (!Array.isArray(existingPerm)) {
    auth.$storage.setState('perm', []);
  }

  auth.__userProfileInitWatcherRegistered = true;
  auth.$storage.watchState('loggedIn', (loggedIn) => {
    if (loggedIn) {
      initializeUserProfile(auth, store);
    } else {
      store.dispatch('user/clearActiveProfile');
      auth.$storage.setState('activeProfile', null);
      auth.$storage.setUniversal('activeProfile', null);
    }
  });
}

function normalizeProfile(user, persistedProfile) {
  if (!user) {
    return null;
  }

  const companyId = user.companyId ?? user.company_id ?? persistedProfile?.company_id;
  const profileId = user.profileId ?? user.profile_id ?? persistedProfile?.profile_id;
  const isKeyUser = user.isKeyUser ?? user.is_key_user ?? persistedProfile?.is_key_user;
  const subContractorId = user.subContractorId ?? user.sub_contractor_id ?? persistedProfile?.sub_contractor_id;
  const isSubcontractor = user.isSubcontractor ?? (subContractorId !== null && subContractorId !== undefined);

  return {
    company_id: companyId,
    profile_id: profileId,
    is_key_user: isKeyUser,
    sub_contractor_id: subContractorId,
    is_subcontractor: isSubcontractor,
  };
}

function normalizeIdentity(user, persistedIdentity) {
  if (!user) {
    return null;
  }

  return {
    uid: user.uid ?? persistedIdentity?.uid,
    firstname: user.firstname ?? persistedIdentity?.firstname,
    lastname: user.lastname ?? persistedIdentity?.lastname,
    email: user.email ?? persistedIdentity?.email,
  };
}

/**
 * Initialize the user profile in the store from auth user data
 */
function initializeUserProfile(auth, store) {
  if (!auth || !auth.loggedIn) {
    console.log('[User Profile Init] User not logged in');
    return;
  }

  if (!auth.user) {
    console.log('[User Profile Init] No user object');
    return;
  }

  const user = auth.user;
  const persistedProfile =
    auth.$storage?.getUniversal('activeProfile') ||
    auth.$storage?.getState('activeProfile') ||
    null;
  const normalizedProfile = normalizeProfile(user, persistedProfile);

  const persistedIdentity =
    auth.$storage?.getUniversal('userIdentity') ||
    auth.$storage?.getState('userIdentity') ||
    null;
  const normalizedIdentity = normalizeIdentity(user, persistedIdentity);

  const needsProfileHydration =
    normalizedProfile &&
    normalizedProfile.company_id != null &&
    normalizedProfile.profile_id != null &&
    (user.companyId == null || user.profileId == null);

  const needsIdentityHydration =
    normalizedIdentity &&
    (normalizedIdentity.firstname != null || normalizedIdentity.lastname != null) &&
    (user.firstname == null || user.lastname == null);

  if (needsProfileHydration || needsIdentityHydration) {
    // Hydrate selected profile / display name back onto auth user on refresh.
    auth.setUser({
      ...user,
      ...(needsProfileHydration && {
        companyId: normalizedProfile.company_id,
        profileId: normalizedProfile.profile_id,
        isKeyUser: normalizedProfile.is_key_user,
        subContractorId: normalizedProfile.sub_contractor_id,
        isSubcontractor: normalizedProfile.is_subcontractor,
      }),
      ...(needsIdentityHydration && {
        uid: normalizedIdentity.uid,
        firstname: normalizedIdentity.firstname,
        lastname: normalizedIdentity.lastname,
        email: normalizedIdentity.email,
      }),
    });
  }

  if (normalizedIdentity && (normalizedIdentity.firstname != null || normalizedIdentity.lastname != null)) {
    auth.$storage?.setState('userIdentity', normalizedIdentity);
    auth.$storage?.setUniversal('userIdentity', normalizedIdentity);
  }

  console.log('[User Profile Init] Auth user:', {
    companyId: normalizedProfile?.company_id,
    profileId: normalizedProfile?.profile_id,
    isKeyUser: normalizedProfile?.is_key_user,
    subContractorId: normalizedProfile?.sub_contractor_id,
    isSubcontractor: normalizedProfile?.is_subcontractor
  });

  // Only initialize if we have profile data
  if (normalizedProfile?.company_id || normalizedProfile?.profile_id) {
    const profileData = {
      company_id: normalizedProfile.company_id,
      profile_id: normalizedProfile.profile_id,
      is_key_user: normalizedProfile.is_key_user,
      sub_contractor_id: normalizedProfile.sub_contractor_id
    };
    console.log('[User Profile Init] Setting active profile:', profileData);
    store.dispatch('user/setActiveProfile', profileData);
    auth.$storage?.setState('activeProfile', profileData);
    auth.$storage?.setUniversal('activeProfile', profileData);
  } else {
    console.log('[User Profile Init] No profile data to initialize');
  }
}
